import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Warehouse, AlertTriangle, TrendingDown, TrendingUp, ArrowRightLeft } from 'lucide-react';
import { api } from '@/lib/api';
import { fmt } from '@/lib/format';
import { Card, CardHeader, CardBody } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { cn } from '@/lib/cn';
import { NuevoMovimientoModal } from './NuevoMovimientoModal';

type Almacen = {
  id: string;
  clave: string;
  nombre: string;
  tipo: string;
  ubicacion?: string;
  responsable_nombre?: string;
  total_articulos: number;
  valor_inventario: number;
  activo: boolean;
};

type Existencia = {
  articulo_id: string;
  articulo_clave: string;
  articulo_descripcion: string;
  unidad?: string;
  almacen_id: string;
  almacen_clave: string;
  cantidad: number;
  costo_promedio: number;
  valor_total: number;
  en_reorden: boolean;
  bajo_minimo: boolean;
};

type Movimiento = {
  id: string;
  folio: string;
  fecha: string;
  tipo: string;
  almacen_clave: string;
  proveedor_nombre?: string;
  unidad_administrativa?: string;
  concepto?: string;
  subtotal: number;
  total: number;
  estado: string;
  asiento_id?: string;
};

type Tab = 'existencias' | 'movimientos' | 'reorden';

export function AlmacenPage() {
  const [tab, setTab] = useState<Tab>('existencias');
  const [almacenFiltro, setAlmacenFiltro] = useState('');
  const [movOpen, setMovOpen] = useState<'entrada' | 'salida' | null>(null);

  const { data: almacenes } = useQuery({
    queryKey: ['almacenes'], queryFn: () => api<Almacen[]>('/almacenes'),
  });
  const { data: existencias } = useQuery({
    queryKey: ['existencias', almacenFiltro, tab],
    queryFn: () => {
      const p = new URLSearchParams();
      if (almacenFiltro) p.set('almacen_id', almacenFiltro);
      if (tab === 'reorden') p.set('solo_reorden', 'true');
      return api<Existencia[]>(`/existencias?${p}`);
    },
    enabled: tab !== 'movimientos',
  });
  const { data: movimientos, refetch: refetchMov } = useQuery({
    queryKey: ['movimientos', almacenFiltro],
    queryFn: () => {
      const p = new URLSearchParams();
      p.set('limit', '100');
      if (almacenFiltro) p.set('almacen_id', almacenFiltro);
      return api<Movimiento[]>(`/almacen/movimientos?${p}`);
    },
    enabled: tab === 'movimientos',
  });

  const valorTotal = (almacenes ?? []).reduce((a, x) => a + x.valor_inventario, 0);
  const totalArticulos = (almacenes ?? []).reduce((a, x) => a + x.total_articulos, 0);

  return (
    <div className="flex flex-col gap-6">
      <div className="flex items-start justify-between gap-4">
        <div>
          <p className="text-[12px] font-semibold uppercase tracking-[0.14em] text-ink-mute">Recursos Materiales · Almacén</p>
          <h1 className="mt-1 font-display text-[28px] font-bold tracking-tight text-ink">Almacenes e inventarios</h1>
          <p className="mt-1 text-[14px] text-ink-mute">
            Cada movimiento genera póliza automática firmada Ed25519 y afecta presupuesto. Costeo promedio ponderado.
          </p>
        </div>
        <div className="flex gap-2">
          <Button variant="secondary" onClick={() => setMovOpen('entrada')}>
            <TrendingUp size={14} /> Entrada
          </Button>
          <Button variant="secondary" onClick={() => setMovOpen('salida')}>
            <TrendingDown size={14} /> Salida
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-1 gap-4 lg:grid-cols-[repeat(auto-fit,minmax(280px,1fr))]">
        {(almacenes ?? []).map((a) => (
          <button
            key={a.id}
            onClick={() => setAlmacenFiltro(almacenFiltro === a.id ? '' : a.id)}
            className={cn(
              "flex flex-col rounded-lg border bg-surface p-5 text-left shadow-card transition-all hover:shadow-lift",
              almacenFiltro === a.id ? "border-brand-500" : "border-border"
            )}
          >
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2.5">
                <div className="flex h-9 w-9 items-center justify-center rounded-md bg-brand-50 text-brand-600 dark:bg-brand-900/40">
                  <Warehouse size={16} />
                </div>
                <div>
                  <div className="num text-[11px] font-semibold uppercase tracking-[0.08em] text-ink-mute">{a.clave}</div>
                  <div className="text-[14px] font-semibold text-ink">{a.nombre}</div>
                </div>
              </div>
              <span className="rounded border border-border bg-canvas px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-ink-mute">
                {a.tipo}
              </span>
            </div>
            <div className="mt-4 flex items-end justify-between">
              <div>
                <div className="num text-[22px] font-semibold text-ink">{fmt.money(a.valor_inventario)}</div>
                <div className="text-[11px] text-ink-mute">{a.total_articulos} artículos en stock</div>
              </div>
            </div>
          </button>
        ))}
      </div>

      <div className="flex items-center gap-3 border-b border-border">
        <TabBtn active={tab === 'existencias'} onClick={() => setTab('existencias')}>Existencias</TabBtn>
        <TabBtn active={tab === 'reorden'} onClick={() => setTab('reorden')}>
          Alertas de reorden
        </TabBtn>
        <TabBtn active={tab === 'movimientos'} onClick={() => setTab('movimientos')}>Movimientos</TabBtn>
        <div className="ml-auto text-[12px] text-ink-mute">
          Total: <span className="num font-semibold text-ink">{fmt.money(valorTotal)}</span> en {totalArticulos} artículos
        </div>
      </div>

      {tab !== 'movimientos' && (
        <Card>
          <CardBody className="p-0">
            {!existencias || existencias.length === 0 ? (
              <EmptyExistencias tab={tab} />
            ) : (
              <div className="overflow-x-auto">
                <table className="min-w-full text-[13px]">
                  <thead className="bg-muted/60 text-left text-[11px] font-semibold uppercase tracking-[0.08em] text-ink-mute">
                    <tr>
                      <th className="px-5 py-3">Almacén</th>
                      <th className="px-5 py-3">Artículo</th>
                      <th className="px-5 py-3 text-right">Existencia</th>
                      <th className="px-5 py-3 text-right">Costo prom.</th>
                      <th className="px-5 py-3 text-right">Valor</th>
                      <th className="px-5 py-3">Estado</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-border">
                    {existencias.map((e) => (
                      <tr key={`${e.almacen_id}-${e.articulo_id}`} className="transition-colors hover:bg-muted/50">
                        <td className="num px-5 py-3 text-ink-mute">{e.almacen_clave}</td>
                        <td className="px-5 py-3">
                          <div className="num text-[11px] font-semibold text-brand-700 dark:text-brand-300">{e.articulo_clave}</div>
                          <div className="text-[12px] text-ink">{e.articulo_descripcion}</div>
                        </td>
                        <td className="num px-5 py-3 text-right font-semibold">
                          {fmt.num(e.cantidad)} {e.unidad}
                        </td>
                        <td className="num px-5 py-3 text-right">{fmt.money(e.costo_promedio)}</td>
                        <td className="num px-5 py-3 text-right font-semibold">{fmt.money(e.valor_total)}</td>
                        <td className="px-5 py-3">
                          {e.bajo_minimo ? (
                            <span className="inline-flex items-center gap-1 rounded-full bg-rose-50 px-2 py-0.5 text-[11px] font-medium text-rose-700 dark:bg-rose-900/40 dark:text-rose-200">
                              <AlertTriangle size={11} /> Bajo mínimo
                            </span>
                          ) : e.en_reorden ? (
                            <span className="rounded-full bg-amber-50 px-2 py-0.5 text-[11px] font-medium text-amber-800 dark:bg-amber-900/40 dark:text-amber-200">
                              Reorden
                            </span>
                          ) : (
                            <span className="rounded-full bg-emerald-50 px-2 py-0.5 text-[11px] font-medium text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-200">
                              OK
                            </span>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </CardBody>
        </Card>
      )}

      {tab === 'movimientos' && (
        <Card>
          <CardHeader title="Movimientos recientes" subtitle="Cada movimiento tiene póliza firmada y afectación presupuestal" />
          <CardBody className="p-0">
            {!movimientos || movimientos.length === 0 ? (
              <div className="p-10 text-center text-[13px] text-ink-mute">Sin movimientos.</div>
            ) : (
              <div className="overflow-x-auto">
                <table className="min-w-full text-[13px]">
                  <thead className="bg-muted/60 text-left text-[11px] font-semibold uppercase tracking-[0.08em] text-ink-mute">
                    <tr>
                      <th className="px-5 py-3">Folio</th>
                      <th className="px-5 py-3">Fecha</th>
                      <th className="px-5 py-3">Tipo</th>
                      <th className="px-5 py-3">Almacén</th>
                      <th className="px-5 py-3">Tercero / Área</th>
                      <th className="px-5 py-3">Concepto</th>
                      <th className="px-5 py-3 text-right">Total</th>
                      <th className="px-5 py-3">Póliza</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-border">
                    {movimientos.map((m) => (
                      <tr key={m.id} className="transition-colors hover:bg-muted/50">
                        <td className="num px-5 py-3 font-semibold">{m.folio}</td>
                        <td className="num px-5 py-3">{fmt.date(m.fecha)}</td>
                        <td className="px-5 py-3">
                          <span className={cn(
                            'rounded px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide',
                            m.tipo.startsWith('entrada')
                              ? 'bg-emerald-50 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-200'
                              : 'bg-rose-50 text-rose-700 dark:bg-rose-900/40 dark:text-rose-200'
                          )}>
                            {m.tipo.replace(/_/g, ' ')}
                          </span>
                        </td>
                        <td className="num px-5 py-3 text-ink-mute">{m.almacen_clave}</td>
                        <td className="px-5 py-3 text-ink-mute">
                          {m.proveedor_nombre ?? m.unidad_administrativa ?? '—'}
                        </td>
                        <td className="px-5 py-3 max-w-[240px] truncate">{m.concepto ?? '—'}</td>
                        <td className="num px-5 py-3 text-right font-semibold">{fmt.money(m.total)}</td>
                        <td className="px-5 py-3">
                          {m.asiento_id ? (
                            <span className="rounded-full bg-emerald-50 px-2 py-0.5 text-[11px] font-medium text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-200">
                              Vinculada
                            </span>
                          ) : (
                            <span className="text-[11px] text-ink-soft">—</span>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </CardBody>
        </Card>
      )}

      <NuevoMovimientoModal
        open={movOpen !== null}
        tipo={movOpen ?? 'entrada'}
        onClose={() => setMovOpen(null)}
        onCreated={() => { setMovOpen(null); refetchMov(); }}
      />
    </div>
  );
}

function TabBtn({ active, children, onClick }: { active: boolean; children: React.ReactNode; onClick: () => void }) {
  return (
    <button
      onClick={onClick}
      className={cn(
        'relative px-4 py-2.5 text-[13px] font-medium transition-colors',
        active ? 'text-brand-600' : 'text-ink-mute hover:text-ink'
      )}
    >
      {children}
      {active && <span className="absolute inset-x-0 -bottom-px h-0.5 bg-brand-500" />}
    </button>
  );
}

function EmptyExistencias({ tab }: { tab: Tab }) {
  return (
    <div className="flex flex-col items-center gap-3 p-10 text-center">
      <ArrowRightLeft size={26} className="text-ink-soft" />
      <p className="text-[13px] text-ink-mute">
        {tab === 'reorden' ? 'Sin artículos en punto de reorden.' : 'Sin existencias registradas.'}
      </p>
    </div>
  );
}
